import {Chip, createStyles, Group, NumberInput, SegmentedControl, Select, Stack, Text} from '@mantine/core';
import React from 'react';

import {TimeTriggerConfig} from './RoutineTypes';
import {
  buildRunTime,
  DEFAULT_RUN_TIME_STRING,
  formatTimezoneAbbreviation,
  parseRunTime,
} from './routineUtils';

interface TimeTriggerConfigFormProps {
  config: Partial<TimeTriggerConfig>;
  onChange: (updates: Partial<TimeTriggerConfig>) => void;
}

const FREQ_OPTIONS = [
  {value: 'daily', label: 'Day'},
  {value: 'weekly', label: 'Week'},
  {value: 'monthly', label: 'Month'},
];

const DAY_OPTIONS = [
  {value: '1', label: 'Mondays'},
  {value: '2', label: 'Tuesdays'},
  {value: '3', label: 'Wednesdays'},
  {value: '4', label: 'Thursdays'},
  {value: '5', label: 'Fridays'},
  {value: '6', label: 'Saturdays'},
  {value: '0', label: 'Sundays'},
];

// The "At" row mixes NumberInput and SegmentedControl, whose size tokens don't resolve to the
// same pixel height, so we pin the hour/minute inputs to this value. The SegmentedControl uses
// its `xs` token, which the theme already renders at this height (24px) without label clipping.
const AT_ROW_CONTROL_HEIGHT = 24;

// Pins the hour/minute inputs' height to the shared row height so they line up with the AM/PM toggle.
const AT_ROW_INPUT_STYLES = {
  input: {height: AT_ROW_CONTROL_HEIGHT, minHeight: AT_ROW_CONTROL_HEIGHT},
};

const useStyles = createStyles((theme) => ({
  fieldRow: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing.sm,
    flexWrap: 'wrap' as const,
  },
  fieldLabel: {
    fontSize: theme.fontSizes.sm,
    fontWeight: 500,
    flexShrink: 0,
  },
  chipGroup: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 8,
  },
  // Width is snug for a 1-2 digit value; the default input width is awkwardly wide here.
  timeInput: {
    width: 40,
  },
  pillChip: {
    '& .mantine-Chip-label': {
      borderRadius: 16,
      // The Chip's inner element is a <label>, which the monolith's global bootstrap CSS gives
      // `margin-bottom: 5px`. That margin pushes the pill up and leaves a gap below it, so we
      // zero it here to keep the pill vertically centered in the row.
      marginBottom: 0,
      padding: '0 12px',
      fontSize: theme.fontSizes.xs,
      fontWeight: 500,
      cursor: 'pointer',
    },
    '& .mantine-Chip-iconWrapper': {
      display: 'none',
    },
  },
}));

export default function TimeTriggerConfigForm({config, onChange}: TimeTriggerConfigFormProps): JSX.Element {
  const {classes} = useStyles();

  const weeklyDaysAsStrings = (config.weekly_days ?? []).map((d) => String(d));
  const parsed = parseRunTime(config.run_time ?? DEFAULT_RUN_TIME_STRING);

  return (
    <Stack spacing="md">
      <div className={classes.fieldRow}>
        <Text className={classes.fieldLabel}>Repeat every</Text>
        <NumberInput
          value={config.recurrence_interval ?? 1}
          onChange={(val) => {
            onChange({recurrence_interval: typeof val === 'number' ? val : 1});
          }}
          min={1}
          max={52}
          size="sm"
          sx={{width: 64}}
        />
        <Select
          value={config.freq_type ?? 'weekly'}
          onChange={(val) => {
            if (val) {
              onChange({
                freq_type: val as TimeTriggerConfig['freq_type'],
                weekly_days: val === 'weekly' ? (config.weekly_days ?? []) : [],
              });
            }
          }}
          data={FREQ_OPTIONS}
          size="sm"
          sx={{width: 110}}
          // TODO(nweeden): Dropdown has extra gap due to global Popover withArrow default
        />
      </div>

      {(config.freq_type ?? 'weekly') === 'weekly' && (
        <Group spacing="sm" align="center">
          <Text className={classes.fieldLabel}>On</Text>
          <Chip.Group
            multiple={true}
            value={weeklyDaysAsStrings}
            onChange={(vals: Array<string>) => {
              onChange({weekly_days: vals.map((v) => Number(v))});
            }}
          >
            <div className={classes.chipGroup}>
              {DAY_OPTIONS.map((day) => (
                <Chip
                  key={day.value}
                  value={day.value}
                  size="md"
                  variant="filled"
                  className={classes.pillChip}
                >
                  {day.label}
                </Chip>
              ))}
            </div>
          </Chip.Group>
        </Group>
      )}

      <Stack spacing={4}>
        <Group spacing="sm" align="center">
          <Text className={classes.fieldLabel}>At</Text>
          <Group spacing="xs" align="center">
            <NumberInput
              aria-label="Hour"
              value={parsed.hour12}
              onChange={(val) => {
                onChange({
                  run_time: buildRunTime(typeof val === 'number' ? val : 8, parsed.minute, parsed.period),
                });
              }}
              min={1}
              max={12}
              size="sm"
              className={classes.timeInput}
              styles={AT_ROW_INPUT_STYLES}
              hideControls={true}
            />
            <Text>:</Text>
            <NumberInput
              aria-label="Minute"
              value={parsed.minute}
              onChange={(val) => {
                onChange({
                  run_time: buildRunTime(parsed.hour12, typeof val === 'number' ? val : 0, parsed.period),
                });
              }}
              min={0}
              max={59}
              size="sm"
              className={classes.timeInput}
              styles={AT_ROW_INPUT_STYLES}
              hideControls={true}
              formatter={(value) => String(value).padStart(2, '0')}
            />
            <SegmentedControl
              aria-label="AM or PM"
              data={['AM', 'PM']}
              value={parsed.period}
              onChange={(val) => {
                onChange({run_time: buildRunTime(parsed.hour12, parsed.minute, val as 'AM' | 'PM')});
              }}
              // xs renders a 24px control (matches AT_ROW_CONTROL_HEIGHT) with no label clipping.
              size="xs"
            />
            {config.timezone != null && config.timezone !== '' && (
              <Text size="sm" color="dimmed">
                {formatTimezoneAbbreviation(config.timezone)}
              </Text>
            )}
          </Group>
        </Group>
        <Text size="xs" color="dimmed">
          Your agent will start working at this time
        </Text>
      </Stack>
    </Stack>
  );
}
